import React, { useState, useEffect } from 'react';
import { getStaff, Staff } from '../services/staffService';
import { 
  addPayrollAdjustment, 
  getPayrollAdjustments, 
  deletePayrollAdjustment 
} from '../services/payrollAdjustmentService';
import { PayrollAdjustment } from '../types';
import { auth } from '../firebase';
import { formatCurrency } from '../lib/formatters';
import { 
  Plus, 
  Search, 
  Filter, 
  Trash2, 
  Loader2, 
  CheckCircle, 
  AlertCircle, 
  X, 
  DollarSign, 
  History, 
  ArrowUpRight, 
  ArrowDownLeft 
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface Props {
  tenantId: string;
}

const CATEGORIES = [
  'Omission Correction',
  'Dispute Override',
  'Discretionary Bonus',
  'True-Up Correction',
  'Other'
];

export const ReconciliationLog: React.FC<Props> = ({ tenantId }) => {
  // Data State
  const [adjustments, setAdjustments] = useState<PayrollAdjustment[]>([]);
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Form State
  const [showForm, setShowForm] = useState(false);
  const [selectedStaffEmail, setSelectedStaffEmail] = useState('');
  const [amount, setAmount] = useState('');
  const [adjustmentType, setAdjustmentType] = useState<'addition' | 'deduction'>('addition');
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [reason, setReason] = useState('');
  const [period, setPeriod] = useState('');

  // Search & Filter State
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [filterType, setFilterType] = useState('All');

  // Messages
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    fetchData();
  }, [tenantId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [adjData, staffData] = await Promise.all([
        getPayrollAdjustments(tenantId),
        getStaff(tenantId)
      ]);
      setAdjustments(adjData);
      setStaffList(staffData);
    } catch (err) {
      console.error('Failed to load reconciliation log data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');

    if (!selectedStaffEmail) {
      setErrorMsg('Please select a staff member.');
      return;
    }

    const numericAmount = parseFloat(amount);
    if (isNaN(numericAmount) || numericAmount <= 0) {
      setErrorMsg('Please enter a valid amount greater than 0.');
      return;
    }

    if (!reason.trim()) {
      setErrorMsg('Please specify a reason for this audit adjustment.');
      return;
    }

    const selectedStaff = staffList.find(s => s.email === selectedStaffEmail);
    if (!selectedStaff) {
      setErrorMsg('Selected staff profile not found.');
      return;
    }

    setSubmitLoading(true);
    try {
      const creatorEmail = auth.currentUser?.email || 'admin@growezy.ai';
      await addPayrollAdjustment(tenantId, {
        staffEmail: selectedStaff.email,
        staffName: selectedStaff.name,
        amount: numericAmount,
        adjustmentType,
        category,
        reason: reason.trim(),
        period: period.trim() || undefined,
        createdBy: creatorEmail
      });

      setSuccessMsg('Payroll adjustment logged successfully and added to the audit trail.');
      
      // Reset Form State
      setSelectedStaffEmail('');
      setAmount('');
      setAdjustmentType('addition');
      setCategory(CATEGORIES[0]);
      setReason('');
      setPeriod('');
      setShowForm(false);

      // Refresh Log List
      const updated = await getPayrollAdjustments(tenantId);
      setAdjustments(updated);

      // Clear success banner after 4 seconds
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err: any) {
      console.error('Failed to save payroll adjustment:', err);
      setErrorMsg('Failed to save adjustment. Ensure you have proper manager privileges.');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this adjustment? This will permanently remove it from the audit log.')) {
      return;
    }

    try {
      await deletePayrollAdjustment(tenantId, id);
      setAdjustments(prev => prev.filter(a => a.id !== id));
      setSuccessMsg('Adjustment removed successfully.');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      console.error('Failed to delete adjustment:', err);
      setErrorMsg('Failed to delete adjustment.');
    }
  };

  // Filtering Logic
  const filteredAdjustments = adjustments.filter(adj => {
    const matchesSearch = 
      adj.staffName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      adj.staffEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      adj.reason.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = filterCategory === 'All' || adj.category === filterCategory;
    const matchesType = filterType === 'All' || adj.adjustmentType === filterType;

    return matchesSearch && matchesCategory && matchesType;
  });

  return (
    <div className="space-y-6" id="payroll-reconciliation-log-container">
      {/* Upper Info Banner */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pb-4 border-b border-gray-100">
        <div>
          <h3 className="text-xl font-black text-gray-900 tracking-tight uppercase italic">Payroll Reconciliation Log</h3>
          <p className="text-xs text-gray-400 font-bold uppercase tracking-widest mt-1">
            Audit manager adjustments, overrides, and bonuses with an immutable compliance trail
          </p>
        </div>
        
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-5 h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold shadow-lg shadow-blue-500/15 transition-all text-sm uppercase tracking-wider"
        >
          {showForm ? <X size={16} /> : <Plus size={16} />}
          <span>{showForm ? 'Cancel' : 'Record Audit Adjustment'}</span>
        </button>
      </div>

      {/* Success / Error Banners */}
      <AnimatePresence>
        {successMsg && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 text-sm font-semibold flex items-center gap-3"
          >
            <CheckCircle className="text-emerald-600 shrink-0" size={18} />
            <span>{successMsg}</span>
          </motion.div>
        )}

        {errorMsg && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-4 bg-rose-50 border border-rose-200 rounded-xl text-rose-800 text-sm font-semibold flex items-center gap-3"
          >
            <AlertCircle className="text-rose-600 shrink-0" size={18} />
            <span>{errorMsg}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Slide-out/Toggle Form for Recording Adjustment */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <form onSubmit={handleSubmit} className="bg-gray-50 border border-gray-100 rounded-2xl p-6 md:p-8 space-y-6">
              <div className="flex items-center gap-2 pb-4 border-b border-gray-100">
                <History className="text-blue-600" size={18} />
                <h4 className="text-sm font-black text-gray-900 uppercase tracking-wider">Record Adjustments & Override Actions</h4>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Employee Dropdown */}
                <div className="space-y-2">
                  <label className="text-xs font-black text-gray-400 uppercase tracking-widest block ml-1">Staff Member</label>
                  <select
                    value={selectedStaffEmail}
                    onChange={e => setSelectedStaffEmail(e.target.value)}
                    className="w-full h-12 px-3 bg-white border border-gray-200 rounded-xl font-bold text-sm outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  >
                    <option value="">-- Choose Employee --</option>
                    {staffList.map(s => (
                      <option key={s.id} value={s.email}>{s.name} ({s.role})</option>
                    ))}
                  </select>
                </div>

                {/* Adjustment Type */}
                <div className="space-y-2">
                  <label className="text-xs font-black text-gray-400 uppercase tracking-widest block ml-1">Type of Adjustment</label>
                  <div className="flex bg-white p-1 rounded-xl border border-gray-200 h-12">
                    <button
                      type="button"
                      onClick={() => setAdjustmentType('addition')}
                      className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg text-xs font-bold transition-all ${
                        adjustmentType === 'addition'
                          ? 'bg-emerald-50 text-emerald-700 font-extrabold'
                          : 'text-gray-400 hover:text-gray-600'
                      }`}
                    >
                      <ArrowUpRight size={14} />
                      Addition (+)
                    </button>
                    <button
                      type="button"
                      onClick={() => setAdjustmentType('deduction')}
                      className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg text-xs font-bold transition-all ${
                        adjustmentType === 'deduction'
                          ? 'bg-rose-50 text-rose-700 font-extrabold'
                          : 'text-gray-400 hover:text-gray-600'
                      }`}
                    >
                      <ArrowDownLeft size={14} />
                      Deduction (-)
                    </button>
                  </div>
                </div>

                {/* Amount */}
                <div className="space-y-2">
                  <label className="text-xs font-black text-gray-400 uppercase tracking-widest block ml-1">Adjustment Amount ($)</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                      <DollarSign size={16} />
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={amount}
                      onChange={e => setAmount(e.target.value)}
                      placeholder="0.00"
                      className="w-full h-12 pl-9 pr-3 bg-white border border-gray-200 rounded-xl font-bold text-sm outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                    />
                  </div>
                </div>

                {/* Category */}
                <div className="space-y-2">
                  <label className="text-xs font-black text-gray-400 uppercase tracking-widest block ml-1">Category</label>
                  <select
                    value={category}
                    onChange={e => setCategory(e.target.value)}
                    className="w-full h-12 px-3 bg-white border border-gray-200 rounded-xl font-bold text-sm outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                {/* Pay Period / Reference Month */}
                <div className="space-y-2">
                  <label className="text-xs font-black text-gray-400 uppercase tracking-widest block ml-1">Pay Period Reference (Optional)</label>
                  <input
                    type="text"
                    placeholder="e.g. 2026-07-01 to 2026-07-15"
                    value={period}
                    onChange={e => setPeriod(e.target.value)}
                    className="w-full h-12 px-3 bg-white border border-gray-200 rounded-xl font-bold text-sm outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  />
                </div>

                {/* Action Button */}
                <div className="flex items-end">
                  <button
                    type="submit"
                    disabled={submitLoading}
                    className="w-full h-12 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 text-white font-bold rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-blue-500/10 cursor-pointer transition-all uppercase text-xs tracking-wider"
                  >
                    {submitLoading ? (
                      <Loader2 className="animate-spin" size={16} />
                    ) : (
                      <Plus size={16} />
                    )}
                    <span>Commit to Log</span>
                  </button>
                </div>
              </div>

              {/* Reason / Notes */}
              <div className="space-y-2">
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest block ml-1">Reason & Audit Context Notes</label>
                <textarea
                  rows={2}
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder="Detail why this payroll override is being entered to maintain transparent manager audit logs..."
                  className="w-full p-3 bg-white border border-gray-200 rounded-xl font-medium text-sm outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none"
                />
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Filter and Search Panel */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 bg-gray-50/50 p-4 rounded-xl border border-gray-100">
        <div className="md:col-span-6 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
            <Search size={16} />
          </div>
          <input
            type="text"
            placeholder="Search adjustments by employee, email, or notes..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full h-10 pl-9 pr-3 bg-white border border-gray-200 rounded-lg text-xs font-semibold outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="md:col-span-3 flex items-center gap-2">
          <Filter size={14} className="text-gray-400 shrink-0" />
          <select
            value={filterCategory}
            onChange={e => setFilterCategory(e.target.value)}
            className="w-full h-10 px-2 bg-white border border-gray-200 rounded-lg text-xs font-semibold outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="All">All Categories</option>
            {CATEGORIES.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        <div className="md:col-span-3 flex items-center gap-2">
          <Filter size={14} className="text-gray-400 shrink-0" />
          <select
            value={filterType}
            onChange={e => setFilterType(e.target.value)}
            className="w-full h-10 px-2 bg-white border border-gray-200 rounded-lg text-xs font-semibold outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="All">All Types</option>
            <option value="addition">Additions Only</option>
            <option value="deduction">Deductions Only</option>
          </select>
        </div>
      </div>

      {/* Main Logs Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-blue-600" size={32} />
        </div>
      ) : (
        <div className="card overflow-hidden border border-gray-100 rounded-2xl shadow-sm">
          {/* Desktop Table view */}
          <div className="hidden md:block">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-gray-50/70 border-b border-gray-100">
                  <th className="px-6 py-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Timestamp</th>
                  <th className="px-6 py-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Employee</th>
                  <th className="px-6 py-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Pay Period</th>
                  <th className="px-6 py-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Notes & Reason</th>
                  <th className="px-6 py-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Logged By</th>
                  <th className="px-6 py-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider text-right">Adjustment</th>
                  <th className="px-6 py-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredAdjustments.map((record) => {
                  const isAddition = record.adjustmentType === 'addition';
                  const dateStr = record.timestamp?.toDate 
                    ? record.timestamp.toDate().toLocaleString() 
                    : (record as any).formattedTimestamp || 'Pending...';

                  return (
                    <tr key={record.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4 text-xs font-semibold text-gray-500 whitespace-nowrap">{dateStr}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col">
                          <span className="text-sm font-bold text-gray-900">{record.staffName}</span>
                          <span className="text-[10px] text-gray-400 font-medium">{record.staffEmail}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          record.category === 'Omission Correction' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                          record.category === 'Dispute Override' ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' :
                          record.category === 'Discretionary Bonus' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                          record.category === 'True-Up Correction' ? 'bg-teal-50 text-teal-700 border border-teal-200' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {record.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-xs font-bold text-gray-600 whitespace-nowrap">{record.period || 'N/A'}</td>
                      <td className="px-6 py-4 text-xs font-medium text-gray-600 max-w-xs truncate" title={record.reason}>
                        {record.reason}
                      </td>
                      <td className="px-6 py-4 text-xs font-semibold text-gray-500 whitespace-nowrap">{record.createdBy}</td>
                      <td className="px-6 py-4 text-right whitespace-nowrap">
                        <span className={`text-sm font-black flex items-center justify-end gap-1 ${
                          isAddition ? 'text-emerald-600' : 'text-rose-600'
                        }`}>
                          {isAddition ? '+' : '-'}{formatCurrency(record.amount)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center whitespace-nowrap">
                        <button
                          onClick={() => handleDelete(record.id!)}
                          className="p-1.5 text-gray-400 hover:text-rose-600 rounded-lg hover:bg-rose-50 transition-all cursor-pointer"
                          title="Delete Adjustment"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {filteredAdjustments.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-sm text-gray-400 italic">
                      No payroll audit adjustments found matching filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile view cards */}
          <div className="md:hidden divide-y divide-gray-50">
            {filteredAdjustments.map((record) => {
              const isAddition = record.adjustmentType === 'addition';
              const dateStr = record.timestamp?.toDate 
                ? record.timestamp.toDate().toLocaleDateString() 
                : (record as any).formattedTimestamp || 'Pending...';

              return (
                <div key={record.id} className="p-4 space-y-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-bold text-gray-900">{record.staffName}</p>
                      <p className="text-[10px] text-gray-400">{record.staffEmail}</p>
                    </div>
                    <span className={`text-sm font-black ${isAddition ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {isAddition ? '+' : '-'}{formatCurrency(record.amount)}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-[10px] font-bold">
                      {record.category}
                    </span>
                    {record.period && (
                      <span className="text-[10px] text-gray-500 font-bold">
                        Period: {record.period}
                      </span>
                    )}
                  </div>

                  <div className="p-2 bg-gray-50 rounded-lg border border-gray-100/50">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Reason</p>
                    <p className="text-xs font-semibold text-gray-700 mt-0.5">{record.reason}</p>
                  </div>

                  <div className="flex justify-between items-center text-[10px] text-gray-400 pt-1 border-t border-gray-50">
                    <span>Logged by: {record.createdBy}</span>
                    <div className="flex items-center gap-2">
                      <span>{dateStr}</span>
                      <button
                        onClick={() => handleDelete(record.id!)}
                        className="text-gray-400 hover:text-rose-600 p-1"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
            {filteredAdjustments.length === 0 && (
              <div className="p-8 text-center text-sm text-gray-400 italic">
                No payroll audit adjustments found.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
